/**
 * LearningPointsEditor — Add/remove learning points for course-type activities.
 * Shows a hint "En kurs blir bättre med 2–5 punkter" and allows simple add/remove.
 */
import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { FormDescription, FormItem, FormLabel } from '@/components/ui/form';

interface LearningPointsEditorProps {
  value: string[];
  onChange: (points: string[]) => void;
}

export function LearningPointsEditor({ value, onChange }: LearningPointsEditorProps) {
  const [draft, setDraft] = useState('');

  const addPoint = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    onChange([...value, trimmed]);
    setDraft('');
  };

  const removePoint = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addPoint();
    }
  };

  return (
    <FormItem>
      <FormLabel>Det här lär du dig</FormLabel>
      <FormDescription className="text-xs">
        Beskriv vad deltagarna tar med sig. En kurs blir bättre med 2–5 punkter.
      </FormDescription>

      {value.length > 0 && (
        <ul className="space-y-1.5 mt-2">
          {value.map((point, i) => (
            <li key={i} className="flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm">
              <span className="flex-1 truncate">{point}</span>
              <button
                type="button"
                onClick={() => removePoint(i)}
                className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
                aria-label={`Ta bort "${point}"`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex gap-2 mt-2">
        <Input
          placeholder="T.ex. Laga mat på vedspis"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-1"
        />
        <Button type="button" size="sm" variant="outline" onClick={addPoint} disabled={!draft.trim()}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </FormItem>
  );
}
